import FindLocationResultState from '../data/schema/FindLocationResultState';
import meetingLocationResponseToLocationPersonaType from 'owa-location-common-well/lib/utils/meetingLocationResponseToLocationPersonaType';
import PlacesSource from 'owa-service/lib/contract/PlacesSource';
import processFindPlacesResult from './processFindPlacesResult';
import setIsSearching from './setIsSearching';
import showHideFindResult from 'owa-location-picker/lib/actions/showHideFindResult';
import store from 'owa-feedback-manager-store/lib/store/locationFeedbackManagerStore';
import throttle from 'lodash-es/throttle';
import TimeSlot from 'owa-service/lib/contract/TimeSlot';
import transactionComplete from 'owa-feedback-manager-actions/lib/actions/transactionComplete';
import updateFindResult from './updateFindResult';
import { action } from 'satcheljs/lib/legacy';
import { findMeetingLocations } from 'owa-calendar-scheduling-service/lib/services/findMeetingLocationsService';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isNullOrWhiteSpace } from 'owa-string-utils';
import { LocationPersonaControlViewState } from 'owa-location-persona';
import { lazyLogServiceCallForDiagnostics } from 'owa-calendar-forms-diagnostics';
import RecurrenceType from 'owa-service/lib/contract/RecurrenceType';
import LocationPersonaType from 'owa-calendar-scheduling-service/lib/schema/LocationPersonaType';
import WebSessionType from 'owa-service/lib/contract/WebSessionType';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';

const MAX_ENTRIES_TYPEDOWN = 5;
const MAX_QUERY_STRING_LENGTH = 255;

let setIsSearchingDebounced = throttle(
    (findLocationResultState: FindLocationResultState, isSearching: boolean) => {
        setIsSearching(findLocationResultState, isSearching);
    },
    150
);

/**
 * Finds the location suggetsions.
 * @param {findLocationResultStateViewState} findLocationResultState: The location well state
 * @param {string} queryString: the trimmed query string from the location well
 * @param {string[]} requiredAttendees: the required attendees for the meeting. Used by the find places server command to provide relavant results.
 * @param {string[]} optionalAttendees: optional attendees for the meeting. Used by the find places server command to provide relevant results.
 * @param {TimeSlot} timeSlot: the meeting time. Used by the find places server command to provide relevent results
 * @param {boolean} requestRoomsOnly: boolean indicating if request should return only conference rooms
 * @param {boolean} isPrefetchRequest: boolean indicating if request is for prefetching location suggestions
 */
export default action('findLocation')(async function findLocation(
    findLocationResultState: FindLocationResultState,
    queryString: string,
    requiredAttendees: string[] = [],
    optionalAttendees: string[] = [],
    timeSlot?: TimeSlot,
    requestRoomsOnly?: boolean,
    currentDevicePosition?: Position,
    isPrefetchRequest?: boolean,
    recurrence?: RecurrenceType
): Promise<LocationPersonaControlViewState[]> {
    // trim the text
    queryString = queryString ? queryString.trim() : '';

    if (
        findLocationResultState &&
        !findLocationResultState.shouldShowFindResult &&
        !isPrefetchRequest
    ) {
        showHideFindResult(findLocationResultState, true /* shouldShowFindResult */);
    }

    let findLocationResult: LocationPersonaControlViewState[] = [];

    if (isNullOrWhiteSpace(queryString) || queryString.length < MAX_QUERY_STRING_LENGTH) {
        findLocationResultState && setIsSearchingDebounced(findLocationResultState, true);

        // For blank query use relevance location, otherwise use both bing locations and relevance
        let useBingLocation = !isNullOrWhiteSpace(queryString);

        const isBusiness =
            getUserConfiguration().SessionSettings.WebSessionType == WebSessionType.Business;

        // Use Directory as a source for non zero-query suggestions for business users.
        let useTenantSearch = isBusiness && !isNullOrWhiteSpace(queryString);

        let availableConferenceRoomsOnly = isBusiness && isNullOrWhiteSpace(queryString);

        let source = useBingLocation
            ? PlacesSource.Relevance | PlacesSource.BingAutosuggest
            : PlacesSource.Relevance;

        if (useTenantSearch) {
            source = source | PlacesSource.Directory;
        }

        let deviceLocation =
            findLocationResultState &&
            findLocationResultState.currentDevicePosition &&
            findLocationResultState.currentDevicePosition.coords;

        // VSTS: 25963 - the locationCommonWell will pass currentDevicePosition to this action directly. When the legacy well gets removed, we can clean up this if check.
        if (currentDevicePosition) {
            deviceLocation = currentDevicePosition.coords;
        }

        let { status, locationTransactionId, findPlacesResult } = await fetchLocationSuggestions(
            queryString,
            source,
            requiredAttendees,
            optionalAttendees,
            timeSlot,
            deviceLocation,
            availableConferenceRoomsOnly,
            requestRoomsOnly,
            recurrence
        );

        if (status == '200') {
            processFindPlacesResult(
                findPlacesResult,
                findLocationResult,
                locationTransactionId,
                queryString
            );

            // Log transactionComplete entity feedback
            let locations: string[] = [];
            let relevanceHints: string[] = [];
            if (findLocationResult) {
                findLocationResult.forEach(location => {
                    locations.push(location.relevanceData.locationFeedbackId);
                    relevanceHints.push(location.relevanceData.relevanceHint);
                });
            }

            transactionComplete(
                store.feedbackManagerState,
                queryString,
                locations,
                locationTransactionId,
                relevanceHints,
                requiredAttendees,
                optionalAttendees
            );
        }

        setIsSearchingDebounced(findLocationResultState, false);

        // Only update and show find result if query string hasn't been changed.
        // If it has been changed, another findpeople call would have been issued and
        // we can let its callback handle update and show find result.
        if (findLocationResultState && findLocationResultState.queryString.trim() == queryString) {
            let findResultCount = findLocationResult.length;
            if (findResultCount > 0) {
                // Remove additional results so that only MAX_ENTRIES_TYPEDOWN number of locations are shown.
                if (findResultCount > MAX_ENTRIES_TYPEDOWN) {
                    findLocationResult.splice(
                        MAX_ENTRIES_TYPEDOWN,
                        findResultCount - MAX_ENTRIES_TYPEDOWN
                    );
                }
            }

            // Clear existing find results and add the new ones.
            updateFindResult(findLocationResultState, findLocationResult);
        }
    }
    return findLocationResult;
});

async function fetchLocationSuggestions(
    queryString: string,
    source: PlacesSource,
    requiredAttendees: string[] = [],
    optionalAttendees: string[] = [],
    timeSlot?: TimeSlot,
    deviceLocation?: Coordinates,
    availableConferenceRoomsOnly?: boolean,
    requestRoomsOnly?: boolean,
    recurrence?: RecurrenceType
) {
    let response;
    let locationTransactionId: string;
    let findPlacesResult: LocationPersonaType[] = [];
    let findMeetingLocationResponse;

    const queryLAS = async (availableConferenceRoomsOnly: boolean) => {
        response = await findMeetingLocations(
            queryString,
            source,
            requiredAttendees,
            optionalAttendees,
            MAX_ENTRIES_TYPEDOWN,
            timeSlot,
            deviceLocation,
            null,
            availableConferenceRoomsOnly,
            requestRoomsOnly,
            recurrence
        );

        if (response && response.status == 200) {
            locationTransactionId = response.headers.get('request-id');
            findMeetingLocationResponse = await response.json();
        }
    };

    await queryLAS(availableConferenceRoomsOnly);

    // If we requested only available conference rooms and no response was returned for zero query.
    // We will re-request the location suggestions without "availableConferenceRoomsOnly" param
    if (
        availableConferenceRoomsOnly &&
        findMeetingLocationResponse &&
        findMeetingLocationResponse.MeetingLocations &&
        findMeetingLocationResponse.MeetingLocations.length == 0
    ) {
        await queryLAS(false /* availableConferenceRoomsOnly */);
    }

    findPlacesResult = meetingLocationResponseToLocationPersonaType(findMeetingLocationResponse);

    // Log the service call to forms diagnostics panel
    if (isFeatureEnabled('cal-mf-diagnostics')) {
        let headers = {};
        // We have to convert response.headers (which is an iterator) into a headers json object
        for (let header of response.headers.entries()) {
            // header[0] is the key and header[1] is the value of the header
            // For example, header[0] = 'x-backendhttpstatus' and header[1] = '200'
            headers[header[0]] = header[1];
        }

        lazyLogServiceCallForDiagnostics.import().then(logServiceCall =>
            logServiceCall({
                action: 'findmeetinglocations',
                status: response ? response.status : 'Error',
                response: findPlacesResult,
                request: {
                    queryString: queryString,
                    source: source,
                    requiredAttendees: requiredAttendees,
                    optionalAttendees: optionalAttendees,
                    timeSlot: timeSlot,
                    deviceLocation: deviceLocation,
                    availableConferenceRoomsOnly: availableConferenceRoomsOnly,
                },
                headers: headers,
            })
        );
    }

    return {
        status: response ? response.status : 'Error',
        locationTransactionId: locationTransactionId,
        findPlacesResult: findPlacesResult,
    };
}
